/**
 * Per-row comparison table: 3 cells per metric — Range A, Range B, Δ (= A − B). Rows are the
 * Range-A rows; Range-B metrics are looked up by the row's stable id. A is prominent, B is the
 * muted baseline, Δ is colored by sign. For Media Devoted | Ciro Autiero
 */

import { Fragment } from 'react';
import type { CSSProperties, FC, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import type { ColumnDef } from '../shared/constants';
import type { GenericStatsRow } from '../shared/generic-stats.types';
import { formatMetricValue, getVoluumValueForDisplay } from '../shared';
import { deltaToneClass, metricCellStyle, metricHeaderStyle } from './compareCell';

type Metrics = Record<string, number> | null | undefined;

const GROUP_BORDER = 'border-l-2 border-border';

interface StatsCompareTableProps {
  rows: GenericStatsRow[];
  compareById: Map<string, Record<string, number> | null>;
  totalsA: Record<string, number>;
  totalsB: Record<string, number>;
  columnDefs: ColumnDef[];
  currency: string;
  labelA: string;
  labelB: string;
  firstColumnLabel: string;
  renderFirstCell: (row: GenericStatsRow) => ReactNode;
  firstColumnClassName?: string;
}

const ValueCells: FC<{ a: Metrics; b: Metrics; col: ColumnDef; currency: string }> = ({
  a,
  b,
  col,
  currency,
}) => {
  const av = getVoluumValueForDisplay(a, col.key);
  const bv = getVoluumValueForDisplay(b, col.key);
  const aNum = typeof av === 'number' ? av : null;
  const bNum = typeof bv === 'number' ? bv : null;
  const hasB = b != null && bNum != null;
  // A = current (primary range), B = baseline (compare range); Δ = current − baseline.
  const delta = (aNum ?? 0) - (bNum ?? 0);
  const aStyle = metricCellStyle(col, aNum);
  const cell = 'px-3 py-2 text-right tabular-nums whitespace-nowrap';
  return (
    <>
      <td className={cn(cell, GROUP_BORDER, aStyle.className)} style={aStyle.style}>
        {formatMetricValue(av, col, currency)}
      </td>
      <td className={cn(cell, 'text-muted-foreground')}>
        {hasB ? formatMetricValue(bv, col, currency) : '—'}
      </td>
      <td className={cn(cell, 'font-medium', hasB ? deltaToneClass(delta) : 'text-muted-foreground')}>
        {hasB ? `${delta > 0 ? '+' : ''}${formatMetricValue(delta, col, currency)}` : '—'}
      </td>
    </>
  );
};

export const StatsCompareTable: FC<StatsCompareTableProps> = ({
  rows,
  compareById,
  totalsA,
  totalsB,
  columnDefs,
  currency,
  labelA,
  labelB,
  firstColumnLabel,
  renderFirstCell,
  firstColumnClassName,
}) => {
  const sub = 'px-3 py-1 text-right text-[10px] font-semibold uppercase tracking-wide';
  const groupHeader: CSSProperties = { backgroundColor: 'hsl(var(--muted))' };
  return (
    <table className="w-full border-collapse text-[13px]">
      <thead className="sticky top-0 z-10">
        <tr className="border-b border-border">
          <th
            rowSpan={2}
            className="sticky left-0 z-20 border-r border-border bg-muted px-3 py-2 text-left align-middle font-semibold text-foreground"
          >
            {firstColumnLabel}
          </th>
          {columnDefs.map((col) => (
            <th
              key={col.key}
              colSpan={3}
              className={cn(GROUP_BORDER, 'px-2 py-1.5 text-center font-semibold text-foreground')}
              style={metricHeaderStyle(col) ?? groupHeader}
            >
              {col.label}
            </th>
          ))}
        </tr>
        <tr className="border-b border-border bg-muted text-muted-foreground">
          {columnDefs.map((col) => (
            <Fragment key={col.key}>
              <th className={cn(sub, GROUP_BORDER)} title={labelA}>A</th>
              <th className={sub} title={labelB}>B</th>
              <th className={sub}>Δ</th>
            </Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={`${row.id}::${i}`}
            className={cn(
              'group border-b border-border/40 transition-colors hover:bg-[hsl(var(--table-row-hover))]',
              i % 2 === 0 ? 'bg-card' : 'bg-[hsl(var(--table-row-zebra))]',
            )}
          >
            <td
              className={cn(
                'sticky left-0 z-20 border-r border-border px-3 py-2 align-top group-hover:bg-[hsl(var(--table-row-hover))]',
                i % 2 === 0 ? 'bg-card' : 'bg-[hsl(var(--table-row-zebra))]',
                firstColumnClassName,
              )}
            >
              {renderFirstCell(row)}
            </td>
            {columnDefs.map((col) => (
              <ValueCells
                key={col.key}
                a={row.voluum}
                b={compareById.get(row.id)}
                col={col}
                currency={currency}
              />
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot className="sticky bottom-0 z-10">
        <tr className="border-t-2 border-border bg-muted font-semibold">
          <td className="sticky left-0 z-20 border-r border-border bg-muted px-3 py-2 text-left">
            Total
          </td>
          {columnDefs.map((col) => (
            <ValueCells key={col.key} a={totalsA} b={totalsB} col={col} currency={currency} />
          ))}
        </tr>
      </tfoot>
    </table>
  );
};

export default StatsCompareTable;
